import { Avatar, AvatarFallback } from "@libs/zapi-ui/ds/components/avatar";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import { TableCell, TableRow } from "@libs/zapi-ui/ds/components/table";
import { TUser } from "../services/schemas";
import { useRevokeInvitation } from "../services/useRevokeInvitation";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { getInitials } from "../lib/getInitials";
import { useTranslation } from "react-i18next";
import { STATUS_KEYS, getMemberRoles } from "../const/const";
import { useResendInvitation } from "../services/useResendInvitation";

type TeamTableRowProps = {
  member: TUser;
  organizationId: number;
  selectedRows: number[];
  handleSelectRow: (id: number) => void;
  currentUserId?: number;
  handleOpenManageAccess: (member: TUser) => void;
};

export function TeamTableRow(props: TeamTableRowProps) {
  const { member, organizationId, selectedRows, handleSelectRow, currentUserId, handleOpenManageAccess } = props;
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const { mutate: cancelInvitation, isPending: isLoadingCancel, isSuccess: isSuccessCancel } = useRevokeInvitation();
  const { mutate: resendInvitation, isPending: isLoadingResend } = useResendInvitation();

  const isCurrentUser = member.id === currentUserId;
  const isPending = member.status === "pending";
  const isExpired = member.status === "expired";
  const actionsDisabled = isCurrentUser || isPending || isExpired;
  const isSelected = selectedRows.includes(member.id);

  function handleCancelInvitation() {
    if (!organizationId) return;
    cancelInvitation(
      { organizationId, email: member.email },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });
          toast.success(t("team.inviteCancelled"));
        },
        onError: (error) => {
          toast.error(error.response?.data.message ?? t("team.inviteCancelFailed"));
        }
      }
    );
  }

  function handleResendInvitation() {
    if (!organizationId) return;
    resendInvitation(
      { organizationId, email: member.email },
      {
        onSuccess: () => {
          toast.success(t("team.inviteResent"));
        },
        onError: (error) => {
          toast.error(error.response?.data.message ?? t("team.inviteResendFailed"));
        }
      }
    );
  }

  if (isSuccessCancel) return null;

  return (
    <TableRow data-state={isSelected ? "selected" : undefined}>
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => handleSelectRow(member.id)}
          aria-label={t("team.selectRow", { name: `${member.firstName} ${member.lastName}` })}
          disabled
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-3">
          <Avatar>
            <AvatarFallback>{getInitials(member.firstName, member.lastName)}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">
              {member.firstName} {member.lastName} {isCurrentUser && t("team.you")}
            </div>
            <div className="text-[14px] text-muted-foreground">{member.email}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>{getMemberRoles(t).find((role) => role.value === member.role)?.label || member.role}</TableCell>
      <TableCell>{t("team.projectCount", { count: member.projects?.length || 0 })}</TableCell>
      <TableCell>
        {STATUS_KEYS[member.status as keyof typeof STATUS_KEYS]
          ? t(STATUS_KEYS[member.status as keyof typeof STATUS_KEYS])
          : member.status}
      </TableCell>
      <TableCell>{member.createdAt.toLocaleDateString()}</TableCell>
      <TableCell>
        {isPending || isExpired ? (
          <div className="flex items-center space-x-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid={`team-table-row-${member.id}-resend-invite-button`}
              disabled={isLoadingCancel || isLoadingResend}
              onClick={handleResendInvitation}
            >
              {isLoadingResend ? t("team.resending") : t("team.resendInvite")}
            </Button>
            {isPending && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                data-testid={`team-table-row-${member.id}-cancel-invite-button`}
                onClick={handleCancelInvitation}
                disabled={isLoadingCancel || isLoadingResend}
              >
                {isLoadingCancel ? t("team.cancelling") : t("team.cancelInvite")}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid={`team-table-row-${member.id}-manage-access-button`}
              onClick={() => handleOpenManageAccess(member)}
              disabled={actionsDisabled || isLoadingResend}
            >
              {t("team.manageAccess")}
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
